/**
 * <bowl-builder>
 * The single Bowl Builder component for the revamp theme. Driven by the
 * product metafield `custom.bowl_builder` (rendered in bowl-builder.liquid).
 *
 * Handles three things, all in one place:
 *   1. PICK groups  — selectable chips (pick_one / pick_many) with min/max
 *      rules. Selections are written to hidden `properties[Title]` inputs that
 *      submit with the main product form (free line-item properties).
 *   2. Required validation — blocks "Add to cart" until every required pick
 *      group is satisfied, with inline messages.
 *   3. UPSELL sheet — an optional paid add-on side sheet. When present, the
 *      main "Add to cart" opens it; the sheet adds the bowl (with its chosen
 *      properties) plus any add-on variants in one /cart/add.js request.
 *
 * No dependency on the legacy poke-station component.
 */
if (!customElements.get('bowl-builder')) {
  class BowlBuilder extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.groups = Array.from(this.querySelectorAll('[data-bowl-group]'));
      this.groups.forEach((g) => this.initGroup(g));

      // Optional upsell sheet (at most one per product).
      this.sheetEl = this.querySelector('[data-bowl-sheet]');
      if (this.sheetEl) this.initSheet();

      this.bindFormGuard();
      window.addEventListener('pageshow', () => this.bindFormGuard());
      this.reflect();
    }

    disconnectedCallback() {
      if (this._portaled && this.sheetEl && this.sheetEl.parentNode === document.body) {
        this.sheetEl.remove();
      }
    }

    /* ---------------- main product form lookups ---------------- */

    getForm() {
      return this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
    }

    getSubmitBtn() {
      return this.dataset.submitId ? document.getElementById(this.dataset.submitId) : null;
    }

    getBowlVariantId() {
      const form = this.getForm();
      const input = form ? form.querySelector('[name="id"]') : null;
      return input && !input.disabled ? input.value : null;
    }

    getBowlQuantity() {
      const form = this.getForm();
      const input = form ? form.querySelector('[name="quantity"]') : null;
      const n = input ? parseInt(input.value, 10) : 1;
      return Number.isFinite(n) && n > 0 ? n : 1;
    }

    /* ---------------- pick groups (chips) ---------------------- */

    initGroup(group) {
      const selection = group.dataset.selection; // 'one' | 'many'
      const max = parseInt(group.dataset.max, 10) || 0; // 0 = no limit
      const chips = Array.from(group.querySelectorAll('[data-bowl-chip]'));

      chips.forEach((chip) => {
        chip.addEventListener('click', () => {
          const pressed = chip.getAttribute('aria-pressed') === 'true';

          if (selection === 'one') {
            // radio behaviour: select this, clear siblings (toggle off only if optional)
            const min = parseInt(group.dataset.min, 10) || 0;
            if (pressed && min === 0) {
              chip.setAttribute('aria-pressed', 'false');
            } else {
              chips.forEach((c) => c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'));
            }
          } else {
            if (pressed) {
              chip.setAttribute('aria-pressed', 'false');
            } else {
              const current = chips.filter((c) => c.getAttribute('aria-pressed') === 'true').length;
              if (max > 0 && current >= max) {
                this.flashLimit(group, max);
                return;
              }
              chip.setAttribute('aria-pressed', 'true');
            }
          }
          this.syncGroup(group);
          this.checkGroup(group);
          this.reflect();
        });
      });

      this.syncGroup(group);
    }

    selectedValues(group) {
      return Array.from(group.querySelectorAll('[data-bowl-chip][aria-pressed="true"]')).map(
        (c) => c.dataset.value
      );
    }

    syncGroup(group) {
      const values = this.selectedValues(group);
      const input = group.querySelector('[data-bowl-input]');
      if (input) {
        input.value = values.join(', ');
        // Don't submit an empty line-item property (disabled inputs are skipped by FormData).
        input.disabled = values.length === 0;
      }
    }

    /** Validate one group against its own rules; show/clear its inline message. */
    checkGroup(group) {
      const min = parseInt(group.dataset.min, 10) || 0;
      const max = parseInt(group.dataset.max, 10) || 0;
      const n = this.selectedValues(group).length;
      const title = group.dataset.title || 'option';
      if (n < min) {
        this.setError(group, min === 1 ? `Please choose a ${title}.` : `Choose at least ${min}.`);
        return false;
      }
      if (max > 0 && n > max) {
        this.setError(group, `Choose up to ${max}.`);
        return false;
      }
      this.setError(group, '');
      return true;
    }

    /** True only when every group satisfies its min/max — i.e. the bowl is built. */
    isComplete() {
      return this.groups.every((g) => {
        const min = parseInt(g.dataset.min, 10) || 0;
        const max = parseInt(g.dataset.max, 10) || 0;
        const n = this.selectedValues(g).length;
        if (n < min) return false;
        if (max > 0 && n > max) return false;
        return true;
      });
    }

    /** Mirror completeness onto the main Add-to-cart button (re-applied after
        the theme re-renders it on variant change). */
    reflect() {
      const btn = this.getSubmitBtn();
      if (!btn) return;
      const incomplete = !this.isComplete();
      btn.classList.toggle('bowl-incomplete', incomplete);
      if (incomplete) {
        btn.setAttribute('aria-disabled', 'true');
      } else {
        btn.removeAttribute('aria-disabled');
      }
    }

    flashLimit(group, max) {
      this.setError(group, `Choose up to ${max}.`);
      clearTimeout(group._flash);
      group._flash = setTimeout(() => {
        const values = this.selectedValues(group);
        const min = parseInt(group.dataset.min, 10) || 0;
        if (values.length >= min) this.setError(group, '');
      }, 1600);
    }

    setError(group, msg) {
      const el = group.querySelector('[data-bowl-error]');
      if (!el) return;
      if (msg) {
        el.textContent = msg;
        el.hidden = false;
        group.setAttribute('data-invalid', '');
      } else {
        el.hidden = true;
        group.removeAttribute('data-invalid');
      }
    }

    /** Returns the first invalid group, or null if every group satisfies its rules. */
    validate() {
      let firstInvalid = null;
      this.groups.forEach((group) => {
        if (!this.checkGroup(group) && !firstInvalid) firstInvalid = group;
      });
      return firstInvalid;
    }

    /** Collect chosen pick selections as a {Title: "a, b"} line-item properties map. */
    collectProperties() {
      const props = {};
      this.groups.forEach((group) => {
        const values = this.selectedValues(group);
        if (values.length) props[group.dataset.title] = values.join(', ');
      });
      return props;
    }

    /* ---------------- form submit guard ----------------------- */

    bindFormGuard() {
      const form = this.getForm();
      if (!form || form === this._boundForm) return;
      this._boundForm = form;
      // The theme re-renders the submit button on variant change — re-apply
      // the built/not-built state afterwards.
      form.addEventListener('change', () => setTimeout(() => this.reflect(), 0));
      form.addEventListener(
        'submit',
        (e) => {
          if (!this.getBowlVariantId()) return; // sold out / disabled — let the page handle it

          const invalid = this.validate();
          if (invalid) {
            e.preventDefault();
            e.stopPropagation();
            invalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const chip = invalid.querySelector('[data-bowl-chip]');
            if (chip) chip.focus();
            return;
          }

          if (this.sheetEl) {
            e.preventDefault();
            e.stopPropagation();
            this.openSheet();
          }
          // else: valid + no upsell → normal submit, properties are already in the form.
        },
        true
      );
    }

    /* ---------------- upsell sheet ---------------------------- */

    initSheet() {
      try {
        this.config = JSON.parse(this.sheetEl.querySelector('[data-bowl-sheet-config]').textContent);
      } catch (e) {
        console.warn('[bowl-builder] invalid upsell config', e);
        this.sheetEl = null;
        return;
      }

      this.panel = this.sheetEl.querySelector('.bowl-sheet__panel');
      this.list = this.sheetEl.querySelector('[data-bowl-sheet-list]');
      this.subtotalEl = this.sheetEl.querySelector('[data-bowl-sheet-subtotal]');
      this.addBtn = this.sheetEl.querySelector('[data-bowl-sheet-add]');
      this.skipBtn = this.sheetEl.querySelector('[data-bowl-sheet-skip]');

      const opts = (this.config.options || []).slice().sort((a, b) => a.position - b.position);
      this.primary = opts[0] || { name: 'Option', values: [] };
      this.size = opts[1] || null;

      this.buildSheetRows();

      this.sheetEl.querySelectorAll('[data-bowl-sheet-close]').forEach((el) =>
        el.addEventListener('click', () => this.closeSheet())
      );
      this.sheetEl.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closeSheet();
      });
      this.addBtn.addEventListener('click', () => this.sheetAddToCart(true));
      if (this.skipBtn) this.skipBtn.addEventListener('click', () => this.sheetAddToCart(false));

      this.updateSheet();

      // Portal to <body> so the product section's transform/overflow can't clip it.
      document.body.appendChild(this.sheetEl);
      this._portaled = true;
      this.sheetEl.hidden = false;
      this.sheetEl.style.display = 'none';
    }

    buildSheetRows() {
      this.rows = [];
      this.primary.values.forEach((name) => {
        const row = document.createElement('div');
        row.className = 'bowl-sheet__row';

        const info = document.createElement('div');
        info.className = 'bowl-sheet__info';
        const label = document.createElement('span');
        label.className = 'bowl-sheet__name';
        label.textContent = name;
        const price = document.createElement('span');
        price.className = 'bowl-sheet__price';
        info.append(label, price);

        const controls = document.createElement('div');
        controls.className = 'bowl-sheet__controls';

        let sizeSelect = null;
        if (this.size && this.size.values.length) {
          sizeSelect = document.createElement('select');
          sizeSelect.className = 'bowl-sheet__size';
          sizeSelect.setAttribute('aria-label', this.size.name + ' for ' + name);
          this.size.values.forEach((sv) => {
            const o = document.createElement('option');
            o.value = sv;
            o.textContent = sv;
            sizeSelect.append(o);
          });
          sizeSelect.addEventListener('change', () => this.updateSheet());
          controls.append(sizeSelect);
        }

        const stepper = document.createElement('div');
        stepper.className = 'bowl-sheet__stepper';
        const dec = document.createElement('button');
        dec.type = 'button';
        dec.className = 'bowl-sheet__dec';
        dec.setAttribute('aria-label', 'Remove one ' + name);
        dec.textContent = '−';
        const qty = document.createElement('input');
        qty.className = 'bowl-sheet__qty';
        qty.type = 'number';
        qty.inputMode = 'numeric';
        qty.min = '0';
        qty.value = '0';
        qty.setAttribute('aria-label', 'Quantity of ' + name);
        const inc = document.createElement('button');
        inc.type = 'button';
        inc.className = 'bowl-sheet__inc';
        inc.setAttribute('aria-label', 'Add one ' + name);
        inc.textContent = '+';

        dec.addEventListener('click', () => { qty.value = String(Math.max(0, (parseInt(qty.value, 10) || 0) - 1)); this.updateSheet(); });
        inc.addEventListener('click', () => { qty.value = String((parseInt(qty.value, 10) || 0) + 1); this.updateSheet(); });
        qty.addEventListener('input', () => {
          let n = parseInt(qty.value, 10);
          if (!Number.isFinite(n) || n < 0) n = 0;
          qty.value = String(n);
          this.updateSheet();
        });

        stepper.append(dec, qty, inc);
        controls.append(stepper);
        row.append(info, controls);
        this.list.append(row);

        this.rows.push({ name, row, price, sizeSelect, qty });
      });
    }

    variantFor(name, sizeSelect) {
      const key = this.size && sizeSelect ? name + '|' + sizeSelect.value : name;
      return this.config.variants ? this.config.variants[key] : null;
    }

    money(cents) {
      if (typeof formatPrice === 'function' && this.config.moneyFormat) {
        return formatPrice(cents, this.config.moneyFormat);
      }
      return '$' + (cents / 100).toFixed(2);
    }

    updateSheet() {
      let subtotal = 0;
      let count = 0;
      this.rows.forEach((r) => {
        const v = this.variantFor(r.name, r.sizeSelect);
        const unavailable = !v || v.available === false;
        r.row.toggleAttribute('data-unavailable', unavailable);
        if (unavailable) {
          r.qty.value = '0';
          r.qty.disabled = true;
          r.price.textContent = 'Unavailable';
          return;
        }
        r.qty.disabled = false;
        r.price.textContent = (v.priceFormatted || this.money(v.price)) + (this.size ? '' : ' / lb');
        const n = parseInt(r.qty.value, 10) || 0;
        subtotal += n * v.price;
        count += n;
      });
      this.subtotalEl.textContent = count ? this.money(subtotal) : '—';
      // Primary add stays disabled until at least one add-on is chosen — use
      // "No thanks" to add the bowl alone.
      this.addBtn.disabled = count === 0;
    }

    collectAddonItems() {
      const items = [];
      this.rows.forEach((r) => {
        const n = parseInt(r.qty.value, 10) || 0;
        if (n <= 0) return;
        const v = this.variantFor(r.name, r.sizeSelect);
        if (v && v.available !== false) items.push({ id: v.id, quantity: n });
      });
      return items;
    }

    /* ----- open / close ----- */

    openSheet() {
      if (this.sheetEl.classList.contains('is-open')) return;
      this.opener = this.getSubmitBtn();
      this.sheetEl.style.display = '';
      void this.sheetEl.offsetWidth; // reflow so the slide-in runs
      this.sheetEl.classList.add('is-open');
      if (typeof bodyScroll === 'object' && bodyScroll.lock) bodyScroll.lock(this.panel);
      if (typeof trapFocus === 'function') trapFocus(this.panel, this.sheetEl.querySelector('.bowl-sheet__close'));
    }

    closeSheet() {
      this.sheetEl.classList.remove('is-open');
      if (typeof bodyScroll === 'object' && bodyScroll.unlock) bodyScroll.unlock(this.panel);
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.opener || null);
    }

    hideSheetInstant() {
      this.sheetEl.classList.remove('is-open');
      this.sheetEl.style.display = 'none';
      if (typeof bodyScroll === 'object' && bodyScroll.unlock) bodyScroll.unlock(this.panel);
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.opener || null);
    }

    openCartDrawer() {
      const drawer = document.querySelector('cart-drawer, #cart-drawer, .cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
      } else if (drawer && drawer.classList) {
        drawer.classList.add('is-visible', 'is-open', 'active', 'open');
        document.documentElement.classList.add('cart-drawer-open');
      } else {
        window.location.href = '/cart';
      }
    }

    /* ----- add to cart (sheet path) ----- */

    sheetAddToCart(includeAddons) {
      const bowlId = this.getBowlVariantId();
      if (!bowlId) return;

      const properties = this.collectProperties();
      const bowlItem = { id: bowlId, quantity: this.getBowlQuantity() };
      if (Object.keys(properties).length) bowlItem.properties = properties;

      const items = [bowlItem];
      if (includeAddons) items.push(...this.collectAddonItems());

      this.setSheetBusy(true);

      const cart =
        document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const sections =
        cart && typeof cart.getSectionsToRender === 'function'
          ? cart.getSectionsToRender().map((s) => s.section)
          : [];

      const addUrl = (this.dataset.addUrl || '/cart/add').replace(/\.js$/, '') + '.js';
      fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections, sections_url: window.location.pathname })
      })
        .then((r) => r.json())
        .then((response) => {
          if (response && response.status) {
            console.warn('[bowl-builder] add failed', response);
            return;
          }
          this.hideSheetInstant();
          this.rows.forEach((r) => { r.qty.value = '0'; });
          this.updateSheet();

          if (cart && typeof cart.renderContents === 'function' && response.sections) {
            cart.renderContents(response);
          } else {
            try {
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS === 'object' && PUB_SUB_EVENTS.cartUpdate) {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bowl-builder' });
              }
            } catch (e) {}
            if (typeof updateCartCounters === 'function') updateCartCounters();
            this.openCartDrawer();
          }
        })
        .catch(() => {
          window.location.href = '/cart';
        })
        .finally(() => {
          this.setSheetBusy(false);
        });
    }

    setSheetBusy(busy) {
      if (this.skipBtn) {
        this.skipBtn.disabled = busy;
        this.skipBtn.classList.toggle('loading', busy);
      }
      this.addBtn.classList.toggle('loading', busy);
      if (busy) {
        this.addBtn.disabled = true;
      } else {
        this.updateSheet(); // restore disabled state from the current selection
      }
    }
  }

  customElements.define('bowl-builder', BowlBuilder);
}
